import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, MapPin, Calendar, Users, Home as HomeIcon, FileText, ClipboardCheck, TrendingUp, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { ROUTE_PATHS } from '@/lib/index';
import { projectInfo, apartmentTypes, timeline } from '@/data/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30,
    },
  },
};

export default function Home() {
  return (
    <Layout>
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.HARMONY_RENDER_MAIN}
            alt="HARMONY Xuân Phương – Phối cảnh tổng thể"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: 'spring', stiffness: 200, damping: 30 }}
          className="relative z-10 container mx-auto px-4 text-center"
        >
          <Badge className="mb-4 text-sm px-4 py-1.5" variant="secondary">
            Dự án Nhà Ở Xã Hội – Lực Lượng Vũ Trang CAND
          </Badge>
          <h1 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight leading-tight max-w-4xl mx-auto">
            Xây Dựng Nhà Ở Cho Lực Lượng Vũ Trang CAND
          </h1>
          <p className="text-lg md:text-xl text-primary font-semibold mb-2">
            Tại Các Ô Đất OCT1, OCT3 – Khu Chức Năng Đô Thị Xuân Phương, TP. Hà Nội
          </p>
          <p className="text-base md:text-lg text-muted-foreground mb-2 max-w-2xl mx-auto">
            Chủ đầu tư: <span className="font-semibold text-foreground">Công ty CP Đầu tư Vietasset</span> (Liên danh Vietasset – Constrexim Số 8)
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="text-lg px-8">
              <Link to={ROUTE_PATHS.ELIGIBILITY}>
                Đăng Ký Ngay
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8">
              <Link to={ROUTE_PATHS.PROGRESS}>
                Xem Tiến Độ
              </Link>
            </Button>
          </div>
        </motion.div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {[
              { icon: <Building2 className="h-10 w-10 mx-auto mb-3 text-primary" />, value: '818', label: 'Tổng căn hộ' },
              { icon: <HomeIcon className="h-10 w-10 mx-auto mb-3 text-primary" />, value: '2', label: 'Khối nhà (OCT1 & OCT3)' },
              { icon: <TrendingUp className="h-10 w-10 mx-auto mb-3 text-primary" />, value: '25', label: 'Tầng nổi + 3 hầm' },
              { icon: <Users className="h-10 w-10 mx-auto mb-3 text-primary" />, value: '2.337', label: 'Dân số dự kiến' },
            ].map((stat, idx) => (
              <motion.div key={idx} variants={itemVariants}>
                <Card className="text-center">
                  <CardContent className="pt-6">
                    {stat.icon}
                    <div className="text-3xl font-bold font-mono mb-1">{stat.value}</div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Bảng quy mô dự án OCT1 & OCT3 */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl font-bold mb-3">Quy Mô Dự Án</h2>
            <p className="text-muted-foreground">Thông số kỹ thuật chi tiết hai khối nhà OCT1 và OCT3</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-primary text-primary-foreground">
                        <th className="p-3 text-left font-semibold">STT</th>
                        <th className="p-3 text-left font-semibold">Ký hiệu ô đất</th>
                        <th className="p-3 text-center font-semibold">Đơn vị</th>
                        <th className="p-3 text-center font-semibold">
                          OCT1<br />
                          <span className="text-xs font-normal opacity-80">(01 khối nhà 25T + 3 hầm)</span>
                        </th>
                        <th className="p-3 text-center font-semibold">
                          OCT3<br />
                          <span className="text-xs font-normal opacity-80">(01 khối nhà 25T + 3 hầm)</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { stt: 1, label: 'Diện tích khu đất', unit: 'm²', oct1: '3.185', oct3: '7.836' },
                        { stt: 2, label: 'Diện tích đất xây dựng', unit: 'm²', oct1: '1.510', oct3: '3.558' },
                        { stt: 3, label: 'Mật độ xây dựng', unit: '%', oct1: '47,4', oct3: '45,4' },
                        { stt: 4, label: 'Tầng cao công trình', unit: 'tầng', oct1: '25', oct3: '25' },
                        { stt: 5, label: 'Tổng diện tích sàn xây dựng', unit: 'm²', oct1: '39.081', oct3: '91.262' },
                        { stt: 6, label: 'Hệ số sử dụng đất', unit: 'lần', oct1: '9,4', oct3: '8,8' },
                        { stt: 7, label: 'Số tầng hầm', unit: 'tầng', oct1: '3', oct3: '3' },
                        { stt: 8, label: 'Tổng số căn hộ', unit: 'căn', oct1: '240', oct3: '578' },
                        { stt: 9, label: 'Dân số', unit: 'người', oct1: '680', oct3: '1.657' },
                      ].map((row) => (
                        <tr key={row.stt} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                          <td className="p-3 font-mono text-muted-foreground text-center">{row.stt}</td>
                          <td className="p-3 font-medium">{row.label}</td>
                          <td className="p-3 text-center text-muted-foreground">{row.unit}</td>
                          <td className="p-3 font-mono font-semibold text-center text-primary">{row.oct1}</td>
                          <td className="p-3 font-mono font-semibold text-center text-primary">{row.oct3}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            <p className="text-xs text-muted-foreground text-center mt-3">
              * Nguồn: Bảng Thông Tin Dự Án – Vietasset (Chủ đầu tư liên danh)
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Giới Thiệu Dự Án</h2>
            <p className="text-sm text-muted-foreground font-mono">Địa điểm: Phường Xuân Phương, TP. Hà Nội | Thời gian: Quý II/2025 – Quý IV/2028</p>
            <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
              Dự án nhà ở xã hội quy mô lớn, đáp ứng nhu cầu an cư cho người dân
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src={IMAGES.HARMONY_RENDER_3D}
                alt="Phối cảnh HARMONY Xuân Phương"
                className="rounded-2xl shadow-lg w-full h-[400px] object-cover"
              />
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.p variants={itemVariants} className="text-lg mb-6 leading-relaxed">
                {projectInfo.description}
              </motion.p>

              <motion.div variants={itemVariants} className="space-y-3">
                <h3 className="text-xl font-semibold mb-4">Tiện ích nổi bật:</h3>
                {projectInfo.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Vị Trí & Hạ Tầng</h2>
            <div className="flex items-center justify-center gap-2 text-lg text-muted-foreground">
              <MapPin className="h-5 w-5 text-primary" />
              <span>Ô đất OCT1, OCT3 – Khu Chức Năng Đô Thị Xuân Phương, Phường Xuân Phương, TP. Hà Nội</span>
            </div>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto"
          >
            {projectInfo.infrastructure.map((item, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Google Maps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-12 max-w-5xl mx-auto"
          >
            <div className="rounded-2xl overflow-hidden shadow-lg border border-border">
              <div className="bg-muted/50 px-5 py-3 flex items-center gap-2 border-b border-border">
                <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                <span className="text-sm font-medium">HARMONY Xuân Phương – Phường Xuân Phương/Phương Canh, Quận Nam Từ Liêm, Hà Nội</span>
                <a
                  href="https://maps.app.goo.gl/rvN1YUPCLYWZxTD19"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto text-xs text-primary hover:underline flex-shrink-0"
                >
                  Mở Google Maps ↗
                </a>
              </div>
              <iframe
                title="Vị trí HARMONY Xuân Phương"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d993.8!2d105.7392!3d21.0277!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x313455d75bece789%3A0x2e0fdba76773215!2zU8OibiBCw7NuZyBYdcOibiBQaMawxqFuZw!5e0!3m2!1svi!2svn!4v1714000000000!5m2!1svi!2svn"
                width="100%"
                height="450"
                style={{ border: 0, display: 'block' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Đường 70', desc: 'Kết nối trực tiếp' },
                { label: 'Đại lộ Thăng Long', desc: 'Kết nối thuận tiện' },
                { label: 'Metro số 7', desc: 'Cách 2 phút' },
                { label: 'Sân bay Nội Bài', desc: 'Cách 40 phút' },
              ].map((item) => (
                <div key={item.label} className="rounded-lg border border-border bg-card p-3 text-center">
                  <p className="text-sm font-semibold text-primary">{item.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Thông Tin Căn Hộ</h2>
            <p className="text-muted-foreground text-lg">Đa dạng diện tích – Tòa OCT1 (240 căn) & OCT3 (578 căn), tổng 818 căn</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-4 font-semibold">Loại căn hộ</th>
                        <th className="text-left p-4 font-semibold">Diện tích</th>
                        <th className="text-left p-4 font-semibold">Giá dự kiến</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apartmentTypes.map((apt, index) => (
                        <tr key={index} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                          <td className="p-4 font-medium">{apt.type}</td>
                          <td className="p-4 font-mono text-muted-foreground">{apt.area}</td>
                          <td className="p-4 font-mono text-primary font-semibold">{apt.estimatedPrice}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            <p className="text-sm text-muted-foreground text-center mt-4">
              * Giá bán theo quy định của Nhà nước dành cho nhà ở xã hội. Liên hệ hotline 0917978466 để biết giá chính xác.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Lịch Trình Dự Án</h2>
            <p className="text-muted-foreground text-lg">QII/2025 khởi công → QI/2026 nhận hồ sơ → QIII/2028 bàn giao → QI/2029 cấp sổ</p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-4xl mx-auto relative"
          >
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-border -translate-x-1/2 hidden md:block" />

            {timeline.map((item, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="relative mb-8 md:mb-12 last:mb-0"
              >
                <div className={`flex items-center gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="h-4 w-4 text-primary" />
                          <span className="font-mono text-sm font-semibold text-primary">{item.quarter}/{item.year}</span>
                        </div>
                        <p className="text-base">{item.milestone}</p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="hidden md:flex w-12 h-12 rounded-full bg-primary items-center justify-center flex-shrink-0 relative z-10">
                    <div className="w-6 h-6 rounded-full bg-primary-foreground" />
                  </div>

                  <div className="flex-1 hidden md:block" />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Khám Phá Thêm</h2>
            <p className="text-muted-foreground text-lg">Tìm hiểu chi tiết về dự án</p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            <motion.div variants={itemVariants}>
              <Link to={ROUTE_PATHS.LEGAL}>
                <Card className="h-full hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer">
                  <CardHeader>
                    <FileText className="h-12 w-12 text-primary mb-3" />
                    <CardTitle>Hồ Sơ Pháp Lý</CardTitle>
                    <CardDescription>
                      Xem các giấy tờ pháp lý đã được phê duyệt
                    </CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Link to={ROUTE_PATHS.ELIGIBILITY}>
                <Card className="h-full hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer">
                  <CardHeader>
                    <ClipboardCheck className="h-12 w-12 text-primary mb-3" />
                    <CardTitle>Đối Tượng Đăng Ký</CardTitle>
                    <CardDescription>
                      Kiểm tra điều kiện và quy trình đăng ký
                    </CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Link to={ROUTE_PATHS.ANNOUNCEMENT}>
                <Card className="h-full hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer">
                  <CardHeader>
                    <Building2 className="h-12 w-12 text-primary mb-3" />
                    <CardTitle>Công Bố Thông Tin</CardTitle>
                    <CardDescription>
                      Thông báo và tin tức mới nhất về dự án
                    </CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Link to={ROUTE_PATHS.PROGRESS}>
                <Card className="h-full hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer">
                  <CardHeader>
                    <TrendingUp className="h-12 w-12 text-primary mb-3" />
                    <CardTitle>Tiến Độ</CardTitle>
                    <CardDescription>
                      Theo dõi tiến độ thi công xây dựng
                    </CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold mb-4">Sẵn Sàng Đăng Ký?</h2>
            <p className="text-xl mb-2 opacity-90 max-w-2xl mx-auto">
              Kiểm tra điều kiện và bắt đầu hành trình sở hữu căn hộ của bạn ngay hôm nay
            </p>
            <p className="text-sm opacity-75 mb-8 max-w-xl mx-auto">
              Ưu tiên Lực lượng vũ trang CAND đang phục vụ tại ngũ và các đối tượng theo Điều 76 Luật Nhà ở 2023
            </p>
            <Button asChild size="lg" variant="secondary" className="text-lg px-8">
              <Link to={ROUTE_PATHS.ELIGIBILITY}>
                Bắt Đầu Đăng Ký
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}